import { useEffect, useState } from 'react'
import {
  createdMatches,
  getProfile,
  mockJoinedMatch,
  mockMatches,
  mockMyMatch,
  mockPastMatches,
  withOverrides,
  type MockMatch,
} from '@/shared/mock/data'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useMyMatches, type MatchView } from '@/features/partidos/use-matches'

// Mis partidos hook — owns the 4 states (loading / empty / error / loaded)
// per tab. Real auth queries the player's own matches (players
// array-contains); the dev bypass keeps the certified mock fixtures.
export type FeedStatus = 'loading' | 'loaded' | 'empty' | 'error'

export type MisTab = 'proximos' | 'pasados'

// The active tab survives push/pop navigation (open a past match, come back,
// still on Pasados) — same rule as the feed filters.
const misMemory = { tab: 'proximos' as MisTab }

/** Force which tab Mis partidos opens on next (e.g. after creating a match,
 *  the new match must be visible — never an unrelated remembered tab). */
export function focusMisTab(tab: MisTab): void {
  misMemory.tab = tab
}


// DEV-only: `?estado=error` forces the error state so the 4-states contract is
// verifiable in walks/reviews (the mock never fails on its own).
function forcedError(): boolean {
  return new URLSearchParams(window.location.search).get('estado') === 'error'
}

export function useMisPartidos() {
  const [base, setBase] = useState<FeedStatus>('loading')
  const [tab, setTabState] = useState<MisTab>(misMemory.tab)
  const mine_ = useMyMatches()
  const setTab = (t: MisTab) => {
    misMemory.tab = t
    setTabState(t)
  }

  useEffect(() => {
    if (!AUTH_BYPASS) return
    const t = setTimeout(() => setBase(forcedError() ? 'error' : 'loaded'), 450)
    return () => clearTimeout(t)
  }, [])

  if (!AUTH_BYPASS) {
    const all: MatchView[] = mine_.data ?? []
    // 'past' is derived in the adapter (start < now, or terminal cancelled).
    const matches =
      tab === 'proximos'
        ? all.filter((m) => m.status === 'active' || m.status === 'full')
        : all.filter((m) => m.status === 'past' || m.status === 'cancelled').reverse()
    const status: FeedStatus = forcedError() || mine_.isError
      ? 'error'
      : mine_.isPending
        ? 'loading'
        : matches.length === 0
          ? 'empty'
          : 'loaded'
    const refresh = () => void mine_.refetch()
    return { status, matches, tab, setTab, refresh, retry: refresh }
  }

  // Mis partidos = ONLY matches the player organizes or joined — never the
  // whole discovery pool. Membership is evaluated on the OVERRIDDEN roster so
  // in-session joins/leaves/creations show up (one fact, one store); the real
  // build swaps this for a Firestore players array-contains query.
  const pool = [...createdMatches, mockMyMatch, mockJoinedMatch, ...mockMatches].map((m) => withOverrides(m))
  const mine = pool.filter(
    (m) => m.organizer === getProfile().name || m.players.some((p) => p.name === getProfile().name),
  )
  const past = mockPastMatches
    .map((m) => withOverrides(m))
    .filter((m) => m.organizer === getProfile().name || m.players.some((p) => p.name === getProfile().name))
  const source: MockMatch[] = tab === 'proximos' ? mine : past
  const matches = base === 'loaded' ? source : []
  const status: FeedStatus = base === 'loaded' && matches.length === 0 ? 'empty' : base

  function refresh() {
    setBase('loading')
    setTimeout(() => setBase(forcedError() ? 'error' : 'loaded'), 450)
  }

  return { status, matches, tab, setTab, refresh, retry: refresh }
}
